import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';

import { useTranslation } from 'hooks/useTranslation';

import { CollaboratorAvatar, Link, LoaderColumn } from 'components';

import styles from './ProfileCollaborators.module.scss';
import { useFeatureFlagState } from 'context';

const getProfileCollaboratorsQuery = gql`
  query getProfileCollaborators($userId: String!) {
    profileByIdentifier(identifier: $userId) {
      ... on Profile {
        topContacts(first: 9) {
          contacts {
            id
            email
            name
            avatarUrl
          }
        }
        groups {
          id
          name
          emoji
        }
      }
    }
  }
`;

export const ProfileCollaborators = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();

  const { error, data } = useQuery(getProfileCollaboratorsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { userId },
  });

  if (data === undefined) return <LoaderColumn />;

  if (error) return <LoaderColumn />;

  const {
    profileByIdentifier: { topContacts: { contacts = [] } = {}, groups },
  } = data;

  return (
    <div className={styles.container}>
      {contacts?.length > 0 && (
        <section className={styles.section}>
          <div className={styles.heading}>
            {t('profile-screen.profile-collaborators.top-collaborators')}
          </div>
          <div className={styles.collabGrid}>
            {contacts.map((collaborator: any) => {
              return (
                <Link
                  className={styles.avatarLink}
                  key={collaborator.id}
                  isLinkButton={false}
                  to={`/profile/${collaborator.id}`}
                >
                  <CollaboratorAvatar
                    bordered={false}
                    size="Large"
                    collaborator={{
                      displayName: collaborator.name,
                      emailAddress: collaborator.email,
                      photoURL: collaborator.avatarUrl,
                    }}
                  />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {groupsFeatureFlag && groups?.length > 0 && (
        <section className={styles.section}>
          <div className={styles.heading}>{t('profile-screen.profile-collaborators.groups')}</div>

          <div className={styles.collabGrid}>
            {groups.map((group: any) => {
              return (
                <Link className={styles.avatarLink} key={group.id} to={`/group/${group.id}`}>
                  <CollaboratorAvatar
                    bordered={false}
                    emoji={group.emoji}
                    type="Group"
                    size="Large"
                    collaborator={{
                      displayName: group.name,
                    }}
                  />
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};
